import React from 'react';
import Seo from '../ui/Seo';
import PageHero from '../sections/PageHero';
import Cta from '../sections/Cta';

// Single source of truth for the details that appear throughout the policy, so
// a change of address, phone, or privacy mailbox is a one-line edit.
const COMPANY         = "Smart Electric Solutions";
const SITE_URL        = "https://smartelectric.solutions";
const POLICY_URL      = `${SITE_URL}/privacy-policy`;
const PRIVACY_EMAIL   = "privacy@smartelectric.solutions";
const PHONE_DISPLAY   = "+1 (305) 205-6409";
const PHONE_HREF      = "tel:+13052056409";
const ADDRESS_LINE1   = "3040 N 29th Ave #1";
const ADDRESS_LINE2   = "Hollywood, FL 33020";
const EFFECTIVE_DATE  = "September 3, 2026";

const Mail = () => <a href={`mailto:${PRIVACY_EMAIL}`} className="animated-link">{PRIVACY_EMAIL}</a>;
const Tel  = () => <a href={PHONE_HREF} className="animated-link">{PHONE_DISPLAY}</a>;

const sections =
[
    {
        id: "who-we-are",
        title: "1. Who We Are",
        body:
        (
            <>
                <p>{COMPANY} (&ldquo;{COMPANY},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is a licensed electrical contracting company headquartered at {ADDRESS_LINE1}, {ADDRESS_LINE2}, United States. We provide residential, commercial, and industrial electrical services, power systems, lighting solutions, energy-efficiency work, and related technology services.</p>
                <p>{COMPANY} is the controller responsible for the personal information described in this Privacy Policy. You can reach us at any time at <Mail /> or <Tel />.</p>
            </>
        )
    },
    {
        id: "scope",
        title: "2. Scope of This Policy",
        body:
        (
            <>
                <p>This Privacy Policy explains what personal information we collect, why we collect it, how we use and share it, how long we keep it, and the choices and rights you have. It applies to:</p>
                <ul>
                    <li>Our website at <a href={SITE_URL} className="animated-link">{SITE_URL}</a> and any subdomains we operate;</li>
                    <li>Our pages, profiles, advertising, lead forms, and messaging on Meta platforms (Facebook, Facebook Messenger, Instagram, and WhatsApp), and any application we operate that connects to the Meta Platform APIs;</li>
                    <li>Our other social media profiles, online forms, email and SMS communications; and</li>
                    <li>Inquiries, estimates, service calls, and project work that begin through any of the above.</li>
                </ul>
                <p>This policy does not apply to the practices of companies we do not own or control, including Meta Platforms, Inc., Google LLC, or any third-party website you reach through a link on our site. Their handling of your information is governed by their own privacy policies.</p>
            </>
        )
    },
    {
        id: "information-we-collect",
        title: "3. Information We Collect",
        body:
        (
            <>
                <h3 className="policy-subhead">3.1 Information you give us directly</h3>
                <ul>
                    <li><strong>Contact and consultation requests.</strong> When you submit our contact form, we collect your name, email address, phone number, and any information you choose to include in the message field.</li>
                    <li><strong>Newsletter sign-ups.</strong> When you subscribe for electrical insights and updates, we collect your name and email address.</li>
                    <li><strong>Direct communications.</strong> When you call, text, email, or message us on a social platform, we collect the contents of that communication and the contact details you use.</li>
                    <li><strong>Project and service information.</strong> If you become a customer, we collect the service address, property and system details, scheduling information, project documentation, photographs of the work area, and billing and payment information necessary to perform and invoice the work.</li>
                </ul>
                <p>We do not ask for, and you should never send us through our website forms, sensitive information such as Social Security numbers, credit or debit card numbers, passwords, government identification numbers, or health information.</p>

                <h3 className="policy-subhead">3.2 Information collected automatically</h3>
                <ul>
                    <li><strong>Log and device data.</strong> IP address, browser type and version, operating system, device type, screen size, language preference, referring URL, the pages you view, and the dates and times of your visits.</li>
                    <li><strong>Cookies and similar technologies.</strong> Small files and identifiers placed on your device to keep the site working, remember preferences, measure traffic, and (where you have consented, or where permitted) support advertising measurement. See <a href="#cookies" className="animated-link">Section 6</a>.</li>
                    <li><strong>Security and anti-abuse data.</strong> Our forms use Google reCAPTCHA, which collects device and behavioral signals to distinguish human visitors from automated abuse.</li>
                </ul>

                <h3 className="policy-subhead">3.3 Information we receive from Meta platforms</h3>
                <p>If you interact with us on Facebook, Instagram, Messenger, or WhatsApp (for example by messaging our page, submitting a Meta lead form, commenting, or granting permissions to an application we operate), we may receive from Meta:</p>
                <ul>
                    <li>Your public profile information (such as name, profile picture, and user ID);</li>
                    <li>The email address and phone number you submit through a Meta lead or instant form, or that you authorize us to receive;</li>
                    <li>The content of messages, comments, and inquiries you send to us;</li>
                    <li>Page and advertising insights in aggregated, de-identified form (such as reach, impressions, and engagement counts).</li>
                </ul>
                <p>We receive this information only after you take an action that authorizes it, and only the categories of information covered by the permissions you have granted. You can review and revoke those permissions at any time in your Facebook or Instagram settings under <em>Settings &rarr; Apps and Websites</em>. Revoking permissions stops future sharing; to delete information we already hold, see <a href="#data-deletion" className="animated-link">Section 10</a>.</p>

                <h3 className="policy-subhead">3.4 Information from other sources</h3>
                <p>We may receive your information from a general contractor, property manager, developer, building owner, or referral partner who engages us on a project where you are the occupant or point of contact, and from publicly available business records and mapping, permitting, and licensing sources used in the ordinary course of construction work.</p>
            </>
        )
    },
    {
        id: "how-we-use",
        title: "4. How We Use Your Information",
        body:
        (
            <>
                <p>We use personal information for the following purposes, and only as described here:</p>
                <ul>
                    <li><strong>To respond to you.</strong> Answering inquiries, scheduling consultations, preparing estimates and proposals, and following up on requests you initiate.</li>
                    <li><strong>To perform our services.</strong> Scheduling and dispatching technicians, completing electrical work, coordinating with contractors and inspectors, permitting, warranty service, invoicing, and payment processing.</li>
                    <li><strong>To communicate with you.</strong> Sending service updates, appointment confirmations, project documentation, and (where you have opted in) newsletters, offers, and SMS messages.</li>
                    <li><strong>To operate and improve our website.</strong> Monitoring performance and reliability, understanding which content is useful, diagnosing errors, and improving navigation and accessibility.</li>
                    <li><strong>For marketing and advertising.</strong> Measuring campaign performance and, where permitted and consented to, delivering relevant advertising. You may opt out at any time.</li>
                    <li><strong>For safety, security, and fraud prevention.</strong> Protecting our site, our people, and our customers against abuse, spam, unauthorized access, and other harmful activity.</li>
                    <li><strong>For legal and compliance purposes.</strong> Meeting licensing, permitting, tax, insurance, warranty, contractual, and building-code recordkeeping obligations, establishing or defending legal claims, and complying with lawful requests.</li>
                </ul>
                <p><strong>We do not sell your personal information, and we do not share it for cross-context behavioral advertising as those terms are defined under United States state privacy laws.</strong> We do not use the contents of your messages, or information obtained from Meta platforms, to build profiles for sale to third parties.</p>
            </>
        )
    },
    {
        id: "sms",
        title: "5. Text Message (SMS) Communications",
        body:
        (
            <>
                <p>When you provide your phone number through our contact form, you consent to receive SMS messages from {COMPANY} relating to your inquiry, your appointments, and your project. Message frequency varies. Message and data rates may apply.</p>
                <ul>
                    <li>Reply <strong>STOP</strong> to any message to unsubscribe from further SMS messages.</li>
                    <li>Reply <strong>HELP</strong> for assistance, or contact us at <Tel />.</li>
                    <li>Consent to receive SMS messages is not a condition of purchasing any goods or services from us.</li>
                    <li>We do not sell or share mobile phone numbers or SMS consent with third parties for their own marketing purposes. Phone numbers are shared only with the messaging providers that deliver messages on our behalf.</li>
                </ul>
            </>
        )
    },
    {
        id: "cookies",
        title: "6. Cookies and Similar Technologies",
        body:
        (
            <>
                <p>We use the following categories of cookies and similar technologies:</p>
                <ul>
                    <li><strong>Strictly necessary.</strong> Required for the site to load, route pages, and secure our forms. These cannot be turned off through our site.</li>
                    <li><strong>Functional.</strong> Remember choices such as language or previously viewed content to improve your experience.</li>
                    <li><strong>Analytics.</strong> Help us understand aggregate traffic patterns and which pages are used, so we can improve the site.</li>
                    <li><strong>Advertising and measurement.</strong> Where enabled, these help us measure the effectiveness of campaigns, including campaigns run on Meta platforms, and may involve a Meta pixel or conversions integration.</li>
                </ul>
                <p>You can control cookies through your browser settings, including blocking or deleting them. Blocking strictly necessary cookies may prevent parts of the site from working. Our site also loads Google reCAPTCHA and Google Maps, which set their own cookies and are governed by the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="animated-link">Google Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="animated-link">Terms of Service</a>.</p>
            </>
        )
    },
    {
        id: "how-we-share",
        title: "7. How We Share Information",
        body:
        (
            <>
                <p>We share personal information only in the following circumstances:</p>
                <ul>
                    <li><strong>Service providers.</strong> Companies that host our website, deliver our email and SMS, process payments, provide mapping and spam protection, manage our customer records, and support our field operations. They may use the information only to provide services to us, under contract, and not for their own purposes.</li>
                    <li><strong>Project participants.</strong> General contractors, developers, property owners or managers, architects, engineers, subcontractors, suppliers, and inspectors, where it is necessary to schedule, perform, permit, inspect, or warranty the work you have requested.</li>
                    <li><strong>Affiliated companies.</strong> The Smart Solutions Group family of companies, where doing so is necessary to fulfill your request or provide a service you have asked for.</li>
                    <li><strong>Advertising and analytics platforms.</strong> In the manner described in Sections 4 and 6, including Meta and Google, generally on an aggregated or de-identified basis or through hashed identifiers used for measurement.</li>
                    <li><strong>Legal and safety.</strong> When we reasonably believe disclosure is required by law, subpoena, or other legal process, or is necessary to protect the rights, property, or safety of {COMPANY}, our customers, our employees, or the public.</li>
                    <li><strong>Business transfers.</strong> In connection with a merger, acquisition, financing, reorganization, or sale of assets, in which case we will require the recipient to honor this Privacy Policy or notify you of any material change.</li>
                </ul>
                <p><strong>We do not sell personal information to data brokers or any other third party.</strong></p>
            </>
        )
    },
    {
        id: "meta-platform-data",
        title: "8. Meta Platform Data",
        body:
        (
            <>
                <p>This section applies specifically to information we obtain through the Meta Platform APIs (Facebook, Instagram, Messenger, and WhatsApp) and describes our commitments regarding that data:</p>
                <ul>
                    <li>We request only the permissions we need to provide the feature you are using, such as responding to your message or receiving a lead form you submitted.</li>
                    <li>We use Meta Platform data only to provide and improve the experience you requested, principally to respond to inquiries and to schedule and deliver electrical services.</li>
                    <li>We do not sell, license, or rent Meta Platform data, and we do not transfer it to any data broker, ad network, or monetization service.</li>
                    <li>We do not use Meta Platform data to make eligibility decisions about credit, insurance, employment, housing, or similar benefits.</li>
                    <li>We store Meta Platform data only as long as needed for the purpose described above and delete it on request as described in <a href="#data-deletion" className="animated-link">Section 10</a>.</li>
                    <li>We apply administrative, technical, and physical safeguards to Meta Platform data equivalent to those applied to all other personal information we hold, and we require any service provider handling it to do the same.</li>
                </ul>
                <p>You can review or revoke the permissions you have granted us at any time from your Meta account settings under <em>Settings &rarr; Apps and Websites</em>. Removing our application there stops any further access to your information.</p>
            </>
        )
    },
    {
        id: "retention",
        title: "9. How Long We Keep Your Information",
        body:
        (
            <>
                <p>We keep personal information only as long as necessary for the purpose it was collected, and then delete it or de-identify it. In general:</p>
                <ul>
                    <li><strong>Inquiries that do not become projects:</strong> up to 24 months from your last contact with us.</li>
                    <li><strong>Newsletter subscribers:</strong> until you unsubscribe, and for a short period afterward to honor your opt-out.</li>
                    <li><strong>SMS records:</strong> for the period required to demonstrate consent and honor opt-out requests.</li>
                    <li><strong>Customer and project records:</strong> for the duration of the engagement and afterward for as long as required by applicable licensing, permitting, building-code, warranty, insurance, tax, and statute-of-limitations obligations.</li>
                    <li><strong>Website log and security data:</strong> typically 12 months or less.</li>
                    <li><strong>Meta Platform data:</strong> only as long as needed to respond to and service your inquiry, and deleted promptly upon a valid deletion request.</li>
                </ul>
                <p>Where we are legally required to retain a record, we will retain only the portion required and restrict its use to that purpose.</p>
            </>
        )
    },
    {
        id: "data-deletion",
        title: "10. Deleting Your Data",
        body:
        (
            <>
                <p>You may ask us to delete the personal information we hold about you at any time, free of charge. There are three ways to do it:</p>
                <ol>
                    <li><strong>Email us.</strong> Send a request to <Mail /> with the subject line <em>&ldquo;Data Deletion Request.&rdquo;</em> Please include the name, email address, and phone number you used with us, and, if your request concerns information we received through Facebook or Instagram, the name on that account.</li>
                    <li><strong>Call us.</strong> Reach us at <Tel /> and ask for a data deletion request to be opened on your behalf.</li>
                    <li><strong>Write to us.</strong> {COMPANY}, Attn: Privacy, {ADDRESS_LINE1}, {ADDRESS_LINE2}, United States.</li>
                </ol>
                <p><strong>Deleting data connected to Facebook or Instagram.</strong> To stop all further sharing at the source, open Facebook or Instagram and go to <em>Settings &amp; Privacy &rarr; Settings &rarr; Apps and Websites</em>, select {COMPANY}, and choose <em>Remove</em>. That revokes our access going forward. To also have us erase what we already received, submit a deletion request using any of the three methods above; we will delete the Meta Platform data associated with you from our systems.</p>
                <p><strong>What happens next.</strong> We verify that the request comes from you (or an authorized agent) before acting on it. We confirm receipt within 10 business days and complete verified requests within 30 days, or within 45 days where the law allows an extension and we tell you why more time is needed. When the work is complete, we confirm in writing what was deleted.</p>
                <p><strong>What we may keep.</strong> We may retain a limited record where the law requires it: for example, completed-project and permitting records, invoices and tax records, warranty documentation, records needed to establish or defend a legal claim, and a minimal suppression record confirming that you asked not to be contacted. Anything retained on this basis is restricted to that purpose only.</p>
            </>
        )
    },
    {
        id: "your-rights",
        title: "11. Your Privacy Rights and Choices",
        body:
        (
            <>
                <p>Regardless of where you live, you may ask us to:</p>
                <ul>
                    <li><strong>Access</strong> the personal information we hold about you and learn how we use and share it;</li>
                    <li><strong>Correct</strong> information that is inaccurate or incomplete;</li>
                    <li><strong>Delete</strong> your information, as described in <a href="#data-deletion" className="animated-link">Section 10</a>;</li>
                    <li><strong>Obtain a copy</strong> of the information you provided to us in a portable format;</li>
                    <li><strong>Opt out</strong> of marketing email (use the unsubscribe link in any message), SMS (reply STOP), and targeted advertising; and</li>
                    <li><strong>Withdraw consent</strong> you previously gave, without affecting processing that already took place.</li>
                </ul>
                <p>To exercise any of these rights, contact us at <Mail /> or <Tel />. We will not discriminate against you for exercising a privacy right; you will not receive different pricing or a lower level of service.</p>

                <h3 className="policy-subhead">Florida residents</h3>
                <p>The Florida Digital Bill of Rights provides Florida consumers with rights to access, correct, delete, and obtain a copy of their personal data, and to opt out of targeted advertising, the sale of personal data, and certain profiling. If we deny your request, you may appeal by replying to our decision at <Mail />; we will respond to an appeal within 60 days and, if we again decline, explain how to contact the Florida Department of Legal Affairs.</p>

                <h3 className="policy-subhead">California residents</h3>
                <p>Under the California Consumer Privacy Act, as amended by the CPRA, California residents have the right to know the categories and specific pieces of personal information we collect, the sources, our business purposes, and the categories of third parties with whom we share it; to request deletion and correction; to opt out of the sale or sharing of personal information; and to limit the use of sensitive personal information. As stated above, <strong>we do not sell or share personal information for cross-context behavioral advertising, and we do not use or disclose sensitive personal information beyond the purposes permitted without a right to limit.</strong> You may use an authorized agent to submit a request, with proof of authorization.</p>

                <h3 className="policy-subhead">Other state privacy laws</h3>
                <p>Residents of other states with comprehensive privacy laws (including Colorado, Connecticut, Virginia, Utah, Texas, Oregon, and Montana) have comparable rights of access, correction, deletion, portability, and opt-out. We honor these requests through the same channels described above.</p>
            </>
        )
    },
    {
        id: "security",
        title: "12. How We Protect Your Information",
        body:
        (
            <>
                <p>We maintain administrative, technical, and physical safeguards designed to protect personal information against loss, misuse, unauthorized access, disclosure, alteration, and destruction. These include encryption of data in transit over HTTPS, access controls that limit personal information to personnel who need it to do their jobs, automated abuse protection on our web forms, vendor due diligence and contractual data-protection commitments, and routine review of our systems.</p>
                <p>No method of transmission over the Internet or method of electronic storage is completely secure, so we cannot guarantee absolute security. If we become aware of a breach affecting your personal information, we will notify you and the appropriate authorities as required by applicable law.</p>
            </>
        )
    },
    {
        id: "childrens-privacy",
        title: "13. Children’s Privacy",
        body:
        (
            <p>Our website and services are intended for adults and are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, contact us at <Mail /> and we will delete it promptly.</p>
        )
    },
    {
        id: "third-party-links",
        title: "14. Third-Party Websites and Services",
        body:
        (
            <>
                <p>Our website links to sites we do not run, including manufacturer and partner websites and our social media profiles. We do not control those sites and are not responsible for their content or privacy practices. Review the privacy policy of any third-party site before providing it with personal information.</p>
                <p>Two kinds of link are <strong>not</strong> third-party sites: our affiliated companies within The Smart Solutions Group, and the Smart Building Dashboard, which is operated by Smart Technology Solutions. Information you provide through those is handled under this policy and the policy of the affiliate concerned.</p>
            </>
        )
    },
    {
        id: "international",
        title: "15. Where Your Information Is Processed",
        body:
        (
            <p>{COMPANY} operates in the United States, and the information we collect is processed and stored in the United States. If you access our website or contact us from outside the United States, you understand that your information will be transferred to and processed in the United States, where data protection laws may differ from those of your country.</p>
        )
    },
    {
        id: "do-not-track",
        title: "16. Do Not Track",
        body:
        (
            <p>Some browsers offer a &ldquo;Do Not Track&rdquo; signal. Because no common industry standard for interpreting these signals has been adopted, our website does not currently respond to them. We do honor recognized opt-out preference signals, such as Global Privacy Control, where applicable law requires it.</p>
        )
    },
    {
        id: "changes",
        title: "17. Changes to This Policy",
        body:
        (
            <p>We may update this Privacy Policy to reflect changes in our practices, our services, or the law. When we do, we will revise the effective date at the top of this page. If the changes are material, we will provide a more prominent notice, such as a notice on our homepage or an email to subscribers. Please review this page periodically. Continuing to use our website after an update takes effect means you accept the revised policy.</p>
        )
    },
    {
        id: "contact",
        title: "18. Contact Us",
        body:
        (
            <>
                <p>If you have questions about this Privacy Policy, want to exercise a privacy right, or wish to make a complaint, contact us:</p>
                <div className="backgroundColor1 p-4 customList my-4">
                    <p className="mb-1"><strong>{COMPANY}</strong></p>
                    <p className="mb-1">Attn: Privacy</p>
                    <p className="mb-1">{ADDRESS_LINE1}</p>
                    <p className="mb-3">{ADDRESS_LINE2}, United States</p>
                    <p className="mb-1">Email: <Mail /></p>
                    <p className="mb-0">Phone: <Tel /></p>
                </div>
                <p>We respond to privacy inquiries within 10 business days and resolve verified requests within 30 days.</p>
            </>
        )
    }
];
export default function PrivacyPolicy() {
    return (
        <>
            <Seo
                title="Privacy Policy"
                description={`How ${COMPANY} collects, uses, shares, retains, and deletes personal information, including data received through Meta platforms, plus your privacy rights and how to request deletion of your data.`}
                keywords="privacy policy, data protection, data deletion, Meta platforms, CCPA, GDPR, Smart Electric Solutions"
                path="/privacy-policy"
                schema={{
                    '@type': 'WebPage',
                    name: 'Privacy Policy',
                    description: 'How Smart Electric Solutions collects, uses, shares, retains, and deletes personal information, including data received through Meta platforms.',
                    url: POLICY_URL,
                    inLanguage: 'en-US',
                    dateModified: '2026-09-03',
                    isPartOf: { '@type': 'WebSite', name: COMPANY, url: SITE_URL },
                    publisher: {
                        '@type': 'Organization',
                        name: COMPANY,
                        url: SITE_URL,
                        telephone: '+1-305-205-6409',
                        email: PRIVACY_EMAIL,
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: ADDRESS_LINE1,
                            addressLocality: 'Hollywood',
                            addressRegion: 'FL',
                            postalCode: '33020',
                            addressCountry: 'US'
                        }
                    }
                }}
            />

            <PageHero
                eyebrow="Legal"
                title="Privacy Policy"
                lead={`Effective ${EFFECTIVE_DATE} · Last updated ${EFFECTIVE_DATE}`}
                crumbs={[{ label: 'Privacy Policy' }]}
            />

            <section className="section">
                <div className="shell prose policy-body">
                    <p>{COMPANY} respects your privacy. This policy explains, in plain language, what personal information we collect, why we collect it, who we share it with, how long we keep it, and how you can access or delete it.</p>

                    <p className="policy-callout">
                        We do not sell your personal information. To have your data deleted, see{' '}
                        <a href="#data-deletion" className="animated-link">Deleting Your Data</a> or email <Mail />.
                    </p>

                    <nav className="policy-nav" aria-label="Privacy policy contents">
                        <h2 className="policy-nav__title">Contents</h2>
                        <ol className="policy-toc">
                            {sections.map(section => (
                                <li key={section.id}>
                                    <a href={`#${section.id}`} className="animated-link">
                                        {section.title.replace(/^\d+\.\s*/, '')}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>

                    {sections.map(section => (
                        <section key={section.id} id={section.id} className="policy-section">
                            <h2 className="policy-heading">{section.title}</h2>
                            {section.body}
                        </section>
                    ))}
                </div>
            </section>

            <Cta
                eyebrow="Questions"
                title="Ask us anything about your data."
                lead="Email our privacy mailbox or call the office. We answer every request, and we will tell you exactly what we hold."
            />
        </>
    );
}
